import React, { useState } from "react";
import PropTypes from "prop-types";
import { DATE_FORMATS, qformat } from "views_shared/app/javascript/utils/dateFormatters";

// Figma puts a type badge above every title, articles included.
const MEDIA_TYPE_LABELS = {
  article: "Article",
  podcast: "Podcast",
  video: "Video",
  whitepaper: "Whitepaper",
  case_study: "Case Study",
};

// The publisher's domain, taken from the article link -- "infoworld.com", not "www.infoworld.com".
// `url` is free text a user typed, so a value the browser cannot parse yields no domain rather than
// an exception that would take the whole profile page down.
function publisherDomain(url) {
  if (!url) return null;

  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

// Row layout from the profile mockups' Media section: thumbnail left (~29:16, .media-item-thumb),
// title and description right, no per-item card. editable adds the reorder/feature/edit/delete row
// -- outline-secondary sm, the chassis every profile-section owner control uses. Up/down move the
// row to its neighbor's list_position (a server-side swap, see MediaItems' moveMediaItem);
// canMoveUp/canMoveDown come from the row's position in the list_position-ordered array. The
// featured star is a fas/far swap (filled vs. outline is the state); a featured row also wears the
// same gradient pill a featured quote does, so the state reads for every viewer.
function MediaItem({
  mediaItem,
  editable = false,
  onEdit = () => {},
  onDelete = () => {},
  onMoveUp = () => {},
  onMoveDown = () => {},
  onToggleFeatured = () => {},
  canMoveUp = false,
  canMoveDown = false,
  disabled = false,
}) {
  // Stores the failed URL (not a boolean) so an edited image_url gets a fresh load attempt.
  const [failedSrc, setFailedSrc] = useState(null);
  const imageUrl = mediaItem.image_url;
  const domain = publisherDomain(mediaItem.url);
  const showImage = Boolean(imageUrl) && failedSrc !== imageUrl;

  // Alyssa's placeholder for a missing or broken image: the thumbnail's box with the site's domain.
  const thumbnail = showImage ? (
    <img src={imageUrl} alt="" onError={() => setFailedSrc(imageUrl)} />
  ) : (
    <span className="paragraph-lg fw-semibold text-neutral-70 text-center text-break">{domain}</span>
  );
  const thumbClassName = showImage
    ? "media-item-thumb"
    : "media-item-thumb d-flex align-items-center justify-content-center p-3 bg-white border-neutral-20";

  const typeLabel = MEDIA_TYPE_LABELS[mediaItem.item_type_string] || null;

  // A footer of where the item ran and when it was added. It gives a row its own identity when the
  // thumbnail is absent or has failed, which is otherwise the point at which two rows look alike.
  // "Added" is stated, never implied: the date is when the owner put the item on their profile, and
  // we hold no publication date for the article itself, so a bare date would be read as the wrong
  // thing. Each part is dropped when its source value is missing, so no separator is ever stranded.
  const addedOn = qformat(mediaItem.created_at, DATE_FORMATS.date, null);
  const metadata = [domain, addedOn && `Added ${addedOn}`].filter(Boolean);

  return (
    <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3 mb-4">
      {(showImage || domain) &&
        (mediaItem.url ? (
          <a
            href={mediaItem.url}
            target="_blank"
            rel="noopener noreferrer"
            className={thumbClassName}
            aria-hidden="true"
            tabIndex={-1}
          >
            {thumbnail}
          </a>
        ) : (
          <div className={thumbClassName}>{thumbnail}</div>
        ))}
      <div className="media-item-body flex-grow-1">
        {(mediaItem.featured || typeLabel) && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            {mediaItem.featured && (
              <span className="qw-featured-badge badge rounded-pill d-inline-flex align-items-center fw-bold paragraph-sm">
                <i className="fas fa-star me-1" />
                Featured
              </span>
            )}
            {typeLabel && <span className="qw-media-type-badge badge">{typeLabel}</span>}
          </div>
        )}
        <div className="fw-semibold">
          {mediaItem.url ? (
            <a
              href={mediaItem.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-body text-decoration-none"
            >
              {mediaItem.title}
            </a>
          ) : (
            mediaItem.title
          )}
        </div>
        {mediaItem.description && <div className="small text-body-secondary mt-1">{mediaItem.description}</div>}
        {metadata.length > 0 && (
          <div className="media-item-meta small text-body-secondary mt-2">{metadata.join(" · ")}</div>
        )}
        {editable && (
          <div className="d-flex mt-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex"
              aria-label="Move media item up"
              onClick={onMoveUp}
              disabled={disabled || !canMoveUp}
            >
              <i className="fas fa-arrow-up" />
            </button>
            &nbsp;
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex"
              aria-label="Move media item down"
              onClick={onMoveDown}
              disabled={disabled || !canMoveDown}
            >
              <i className="fas fa-arrow-down" />
            </button>
            &nbsp;
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex"
              aria-label={mediaItem.featured ? "Unfeature media item" : "Feature media item"}
              onClick={onToggleFeatured}
              disabled={disabled}
            >
              <i className={mediaItem.featured ? "fas fa-star" : "far fa-star"} />
            </button>
            &nbsp;
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex"
              aria-label="Edit media item"
              onClick={onEdit}
              disabled={disabled}
            >
              <i className="fas fa-edit" />
            </button>
            &nbsp;
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex"
              aria-label="Remove media item"
              onClick={onDelete}
              disabled={disabled}
            >
              <i className="fas fa-trash text-destructive-90" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

MediaItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  mediaItem: PropTypes.object.isRequired,
  editable: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
  onToggleFeatured: PropTypes.func,
  canMoveUp: PropTypes.bool,
  canMoveDown: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default MediaItem;
